/**
 * Atlas Theme TypeScript Core
 *
 * Main initialization and control module for the Atlas theme that handles plugin initialization,
 * admin UI management, navigation setup, and global utilities.
 *
 * @namespace AtlasTheme
 * @version 1.0.0
 * @author Atlas Theme Development Team
 *
 * @example
 * ```typescript
 * // Initialize the theme
 * AtlasTheme.init();
 *
 * // Initialize main functionality after DOM ready
 * AtlasTheme.initializeMain();
 *
 * // Refresh plugins after dynamic content changes
 * AtlasTheme.refresh();
 *
 * // Get a specific plugin instance
 * const tabsPlugin = AtlasTheme.getPlugin('tabs');
 *
 **/

// Import Swiper and modules
import { Swiper } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Pagination } from 'swiper/modules';
import { EffectCoverflow } from 'swiper/modules';
import { tabs } from './plugins/tabs';
import { accordions } from './plugins/accordions';
import { modals } from './plugins/modal';
import { ShowHide } from './plugins/showhide';
import { LazyLoad } from './plugins/lazy-load';
import { Calendar } from './plugins/calendar';
import { Alerts } from './plugins/alerts';
import { InvestorSwiper } from './plugins/investor-swiper';
import { SwiperStudio } from './plugins/swiper-studio';
import { BannerCard } from './plugins/banner-card';
import { AccessibilityEnhancements } from './plugins/accessibility';
import { AccessibleNav } from './plugins/accessible-nav';
import { BlogCategories } from './plugins/blog-categories';

interface AtlasSpeechRecognitionEvent extends Event {
  results: {
    0: {
      0: {
        transcript: string;
      };
    };
  };
}

interface AtlasSpeechRecognitionErrorEvent extends Event {
  error?: string;
}

interface AtlasSpeechRecognition {
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onend: (() => void) | null;
  onerror: ((event: AtlasSpeechRecognitionErrorEvent) => void) | null;
  onresult: ((event: AtlasSpeechRecognitionEvent) => void) | null;
  start(): void;
}

type AtlasSpeechRecognitionConstructor = new () => AtlasSpeechRecognition;

interface AtlasSpeechRecognitionWindow extends Window {
  SpeechRecognition?: AtlasSpeechRecognitionConstructor;
  webkitSpeechRecognition?: AtlasSpeechRecognitionConstructor;
}

// Plugin instances - exported for global access
let tabsInstance: tabs;
let accordionsInstance: accordions;
let modalInstance: modals;
let showHideInstance: ShowHide;
let lazyLoadInstance: LazyLoad;
let calendarInstance: Calendar;
let alertsInstance: Alerts;
let investorSwiperInstance: InvestorSwiper;
let swiperStudioInstance: SwiperStudio;
let bannerCardInstance: BannerCard;
let accessibilityEnhancementsInstance: AccessibilityEnhancements | null = null;
let accessibleNavInstance: AccessibleNav | null = null;
let blogCategoriesInstance: BlogCategories;

// When true, nav item toggles are always visible on desktop.
// When false, toggles appear only in keyboard navigation mode.
const alwaysShowAccessibleNavToggle = true;

/**
 * Update aria labels for Swiper slides for accessibility
 * @param swiperInstance - The Swiper instance to update aria labels for
 * @public
 */
function updateAriaLabels(swiperInstance: any): void {
  const slides = swiperInstance.slides;
  const realSlides = swiperInstance.slides.length / swiperInstance.loopedSlides ? swiperInstance.slides.length - (swiperInstance.loopedSlides * 2) : swiperInstance.slides.length;

  slides.forEach((slide: HTMLElement, index: number) => {
    const realIndex = swiperInstance.realIndex;
    const current = index === swiperInstance.activeIndex;
    const slideNumber = ((index - swiperInstance.loopedSlides + realSlides) % realSlides) + 1;

    if (slide.classList.contains('swiper-slide-duplicate')) {
      slide.removeAttribute('aria-label');
      slide.removeAttribute('role');
    } else {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', `Slide ${slideNumber} of ${realSlides}`);
    }

    if (current) {
      slide.setAttribute('aria-hidden', 'false');
      slide.setAttribute('tabIndex', '');
    } else {
      slide.setAttribute('aria-hidden', 'true');
      slide.setAttribute('tabIndex', '-1');
      slide.removeAttribute('aria-label');
    }
  });
}

/**
 * Wait for Swiper to be available before executing callback
 * @param callback - Function to execute when Swiper is available
 * @param timeout - Maximum time to wait in milliseconds (default: 5000)
 * @public
 */
function waitForSwiper(callback: () => void, timeout: number = 5000): void {
  const startTime = Date.now();

  function checkSwiper() {
    if (typeof Swiper !== 'undefined') {
      // Check if DOM is ready too
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback);
      } else {
        callback();
      }
    } else if (Date.now() - startTime < timeout) {
      setTimeout(checkSwiper, 100);
    } else {
      console.warn('Swiper not available after timeout');
    }
  }

  checkSwiper();
}

/**
 * Initialize the Atlas theme
 * Sets up all plugins and global event handlers
 * Should be called once when the theme loads
 */
function init(): void {

  // Initialize core plugins
  initializePlugins();

}

/**
 * Initialize all theme plugins
 * @private
 */
function initializePlugins(): void {
  try {
    tabsInstance = new tabs();
    accordionsInstance = new accordions();
    modalInstance = new modals();
    showHideInstance = new ShowHide();
    alertsInstance = new Alerts();
    investorSwiperInstance = new InvestorSwiper();
    swiperStudioInstance = new SwiperStudio();
    bannerCardInstance = new BannerCard();
    blogCategoriesInstance = new BlogCategories();
  } catch (error) {
    console.error('Error initializing plugins:', error);
  }
}

/**
 * Initialize lazy loading for images
 * Can be called manually to refresh lazy loading after content changes
 * @public
 */
function lazyLoadImages(): void {

  try {
    lazyLoadInstance = new LazyLoad({});

    if (lazyLoadInstance && typeof lazyLoadInstance.lazyLoad === 'function') {
      lazyLoadInstance.lazyLoad();
    } else {
      console.warn('LazyLoad plugin not available or missing lazyLoad method');
    }
  } catch (error) {
    console.error('Error initializing lazy load:', error);
  }
}

/**
 * Refresh all plugins
 * Useful after dynamic content changes
 * @public
 */
function refresh(): void {

  try {
    // Re-initialize plugins that support refresh
    if (tabsInstance && typeof (tabsInstance as any).refresh === 'function') {
      (tabsInstance as any).refresh();
    }

    if (accordionsInstance && typeof (accordionsInstance as any).refresh === 'function') {
      (accordionsInstance as any).refresh();
    }

    blogCategoriesInstance = new BlogCategories();

    // Refresh lazy loading
    lazyLoadImages();

  } catch (error) {
    console.error('Error during theme refresh:', error);
  }
}

/**
 * Get plugin instance by name
 * @param pluginName - Name of the plugin to retrieve
 * @returns The plugin instance or null if not found
 * @public
 */
function getPlugin(pluginName: string): any {
  const plugins: { [key: string]: any } = {
    tabs: tabsInstance,
    accordions: accordionsInstance,
    modal: modalInstance,
    showHide: showHideInstance,
    investorSwiper: investorSwiperInstance,
    swiperStudio: swiperStudioInstance,
    blogCategories: blogCategoriesInstance
  };

  return plugins[pluginName] || null;
}

// Core UI and interaction functionality
// ====================================

/**
 * Initialize main theme functionality
 * Called after DOM is ready
 * @public
 */
function initializeMain(): void {

  setupKeyboardNavigationMode();
  setupAccessibilityEnhancements();
  setupNavigation();
  setupSliders();
  setupImageProcessing();
  setupSearch();
  setupScrollToTop();
  /* setupStickyHeader(); */
  setupSelect();
  /* setupSpeedbumps(); */
  setupContentBanners();
  setupFlipCards();
  setupTexturedBackgrounds();
  setupMiscellaneous();
  setupBenchmarkFormLabels();

}

/**
 * Benchmark Email embeds only use placeholders; add visible labels for each field.
 * @private
 */
function setupBenchmarkFormLabels(): void {
  const selector = '[id^="formbox_screen_subscribe_"] input[placeholder]';

  const addLabels = (): boolean => {
    const inputs = document.querySelectorAll<HTMLInputElement>(selector);
    inputs.forEach((input) => {
      if (!input.id || input.dataset.benchmarkLabelled === 'true') return;
      if (input.type === 'hidden' || input.type === 'submit' || input.type === 'button') return;

      input.dataset.benchmarkLabelled = 'true';
      if (document.querySelector(`label[for="${CSS.escape(input.id)}"]`)) return;

      const label = document.createElement('label');
      label.htmlFor = input.id;
      label.className = 'benchmark-form__label';
      label.textContent = input.placeholder.trim();
      input.insertAdjacentElement('beforebegin', label);
    });
    return inputs.length > 0;
  };

  if (addLabels()) return;

  const observer = new MutationObserver(() => {
    if (addLabels()) observer.disconnect();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  window.setTimeout(() => observer.disconnect(), 15000);
}


/**
 * Track whether the user is navigating with the keyboard
 * @private
 */
function setupKeyboardNavigationMode(): void {
  const root = document.documentElement;
  const keyboardClass = 'using-keyboard-navigation';

  if (root.dataset.keyboardNavigationInitialized === 'true') return;

  const enableKeyboardMode = (event: FocusEvent) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    if (target.matches(':focus-visible')) {
      root.classList.add(keyboardClass);
    }
  };

  const disableKeyboardMode = () => {
    root.classList.remove(keyboardClass);
  };

  document.addEventListener('focusin', enableKeyboardMode);
  document.addEventListener('mousedown', disableKeyboardMode);
  document.addEventListener('pointerdown', disableKeyboardMode);
  document.addEventListener('touchstart', disableKeyboardMode);

  root.dataset.keyboardNavigationInitialized = 'true';
}

function closeSearchBox(): void {
  const searchBox = document.querySelector('.search-box') as HTMLElement | null;

  searchBox?.classList.remove('open');
  document.body.classList.remove('search-open');
  document.querySelectorAll('.search-toggle').forEach(toggle => {
    toggle.setAttribute('aria-expanded', 'false');
  });
}

function closeNavigationMenu(): void {
  const html = document.documentElement;
  const menuButton = document.querySelector('.navigation > .navigation__toggle .navigation__toggleSVG') as HTMLElement | null;
  const menuButtonText = document.querySelector('.menu__button--nav-toggle .menu__button-text') as HTMLElement | null;

  html.classList.remove('nav-wrap-open');
  menuButton?.classList.add('closed');
  menuButton?.classList.remove('open');
  menuButton?.setAttribute('aria-label', 'Open navigation menu');
  if (menuButtonText) menuButtonText.textContent = 'Menu';
}

/**
 * Set up navigation functionality
 * @private
 */
function setupNavigation(): void {
  // Navigation toggle
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    if (target.closest('.navigation__toggle')) {
      const html = document.documentElement;
      const menuButton = document.querySelector('.navigation > .navigation__toggle .navigation__toggleSVG') as HTMLElement;

      html.classList.toggle('nav-wrap-open');

      if (html.classList.contains('nav-wrap-open')) {
        closeSearchBox();
        menuButton?.classList.add('open');
        menuButton?.classList.remove('closed');
        menuButton?.setAttribute('aria-label', 'Close navigation menu');
      } else {
        menuButton?.classList.add('closed');
        menuButton?.classList.remove('open');
        menuButton?.setAttribute('aria-label', 'Open navigation menu');
      }
    }
  });

  // Navigation keypress
  document.addEventListener('keypress', (e) => {
    const target = e.target as HTMLElement;

    if (e.key === 'Enter' && target.closest('.navigation__toggle')) {
      const html = document.documentElement;
      const menuButton = document.querySelector('.menu__button--nav-toggle .menu__button-text') as HTMLElement;

      html.classList.toggle('nav-wrap-open');

      if (html.classList.contains('nav-wrap-open')) {
        closeSearchBox();
        if (menuButton) menuButton.textContent = 'Close';
      } else {
        if (menuButton) menuButton.textContent = 'Menu';
      }
    }
  });

  // Slide navigation functionality
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    if (target.closest('.slide-nav .nav-level1__target')) {
      e.stopPropagation();
      const navLevel1 = document.querySelector('.nav-level1');
      if (navLevel1) navLevel1.classList.toggle('active-nav');
      target.closest('.nav-level1__target')?.classList.toggle('active');
    }

    if (target.closest('.slide-nav .nav-level2__target')) {
      e.stopPropagation();
      const navLevel1 = document.querySelector('.nav-level1');
      const navLevel2 = document.querySelector('.nav-level2');
      if (navLevel1) navLevel1.classList.toggle('active-subnav');
      if (navLevel2) navLevel2.classList.toggle('active-nav');
      target.closest('.nav-level2__target')?.classList.toggle('active');
    }
  });

  // Toggle open class on nav-level1__target when clicked
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const clickedTarget = target.closest('.nav-level1__target') as HTMLElement | null;

    if (clickedTarget) {
      const isOpen = clickedTarget.classList.contains('open');

      // Close all other open targets
      document.querySelectorAll('.nav-level1__target.open').forEach(el => {
        el.classList.remove('open');
      });

      // Toggle the clicked one
      if (!isOpen) {
        clickedTarget.classList.add('open');
      }
    } else if (!target.closest('.nav-level1')) {
      // Click outside navigation — close all open targets
      document.querySelectorAll('.nav-level1__target.open').forEach(el => {
        el.classList.remove('open');
      });
    }
  });

  if (!accessibleNavInstance) {
    accessibleNavInstance = new AccessibleNav('.navigation', 990, alwaysShowAccessibleNavToggle);
  } else {
    accessibleNavInstance.refresh();
  }

  // Handle navigation CTAs
  setupNavigationCTAs();

  // Setup global navigation for mobile
  if (window.innerWidth <= 1024) {
    const globalNavMobile = document.querySelectorAll('.global-nav--mobile');
    const lastTarget = document.querySelector('.navigation .nav-level1 .nav-level1__target:last-of-type');

    globalNavMobile.forEach(nav => {
      if (lastTarget) {
        lastTarget.insertAdjacentElement('afterend', nav);
      }
    });
  }

  // Replace text-only global nav links
  const textOnlyLinks = document.querySelectorAll('.global-nav__links--text-only');
  textOnlyLinks.forEach(link => {
    const span = document.createElement('span');
    span.className = 'global-nav__links--text-only';
    span.textContent = link.textContent || '';
    link.replaceWith(span);
  });

  // Move search toggles to appropriate locations
  setupSearchToggles();
}

/**
 * Set up accessibility enhancements (hash focus + section labeling)
 * @private
 */
function setupAccessibilityEnhancements(): void {
  if (!accessibilityEnhancementsInstance) {
    accessibilityEnhancementsInstance = new AccessibilityEnhancements();
  } else {
    accessibilityEnhancementsInstance.refresh();
  }
}

/**
 * Set up navigation CTAs
 * @private
 */
function setupNavigationCTAs(): void {
  const ctaCards = document.querySelectorAll('.cta-card--nav');

  ctaCards.forEach(card => {
    const cardElement = card as HTMLElement;
    const ctaCardDataId = cardElement.dataset.id;

    if (ctaCardDataId) {
      const targetId = '#' + ctaCardDataId;
      const target = document.querySelector(targetId);

      if (target && target.classList.contains('nav-level1__target')) {
        const navLevel2 = target.querySelector('.nav-level2') as HTMLElement;

        if (navLevel2) {
          const ctaCardHeight = cardElement.offsetHeight;
          navLevel2.style.height = (ctaCardHeight + 155) + 'px';
          navLevel2.appendChild(cardElement);
          navLevel2.classList.add('added-cta');
        }
      }
    }
  });
}

/**
 * Set up search toggles
 * @private
 */
function setupSearchToggles(): void {
  // Desktop search toggle
  const searchToggleDesktop = document.querySelector('.search-toggle--desktop');
  const globalNavDesktop = document.querySelector('.global-nav--desktop .global-nav__links:last-of-type');

  if (searchToggleDesktop && globalNavDesktop) {
    const li = document.createElement('li');
    li.appendChild(searchToggleDesktop);
    globalNavDesktop.appendChild(li);
  }

  // Mobile search toggle
  const searchToggleMobile = document.querySelector('.global-nav--mobile .search-toggle--mobile');
  const globalNavMobile = document.querySelector('.global-nav--mobile .global-nav__links:last-of-type');

  if (searchToggleMobile && globalNavMobile) {
    const li = document.createElement('li');
    li.appendChild(searchToggleMobile);
    globalNavMobile.insertBefore(li, globalNavMobile.firstChild);
  }
}

/**
 * Set up Swiper sliders
 * @private
 */
function setupSliders(): void {
  // Tile slider
  const tileSlider = document.querySelector('.tile-slider');
  if (tileSlider) {
    new Swiper('.tile-slider', {
      modules: [Navigation, Pagination],
      loop: true,
      centerInsufficientSlides: true,
      slidesPerView: 3,
      spaceBetween: 20,
      pagination: {
        enabled: true,
        clickable: true,
        el: '.tile-slider__pagination',
      },
      navigation: {
        nextEl: '.tile-slider__button-next',
        prevEl: '.tile-slider__button-prev',
      },
      breakpoints: {
        0: { slidesPerView: 1 },
        1000: { slidesPerView: 2 },
        1280: { slidesPerView: 3 }
      }
    });
  }

  // Carousel with coverflow effect
  const carousel = document.querySelector('.carousel');
  if (carousel) {
    new Swiper('.carousel', {
      modules: [Navigation, Pagination, EffectCoverflow],
      slidesPerView: 1,
      centeredSlides: true,
      loop: true,
      effect: "coverflow",
      coverflowEffect: {
        rotate: 50,
        stretch: 0,
        depth: 100,
        modifier: 1,
        slideShadows: true,
      },
      navigation: {
        enabled: true,
        prevEl: ".carousel__button-prev",
        nextEl: ".carousel__button-next",
      },
      pagination: {
        enabled: true,
        clickable: true,
        el: ".carousel__pagination",
      }
    });
  }
}

/**
 * Process images for accessibility and responsiveness
 * @private
 */
function setupImageProcessing(): void {
  const images = document.querySelectorAll('img');

  images.forEach(img => {
    const src = img.getAttribute('src') || '';
    let alt = img.getAttribute('alt') || '';
    const filename = src.split('/').reverse()[0];

    // Fix empty or filename-based alt text
    if (alt === '' || alt === undefined || alt === filename) {
      img.setAttribute('alt', '');
    }

    // Handle retina images
    if (img.classList.contains('retina')) {
      img.setAttribute('srcset', src + ' 2x');
      img.style.display = 'block';
    }
  });

  // Add standard link class to links without classes
  const linksWithoutClass = document.querySelectorAll('a:not([class]), a.open-modal:not(.button)');
  linksWithoutClass.forEach(link => link.classList.add('standard-link'));

  // Add standard list class to lists without classes
  const listsWithoutClass = document.querySelectorAll('ul:not([class]), ol:not([class])');
  listsWithoutClass.forEach(list => list.classList.add('standard-list'));

  // Open PDF links in new tab
  const pdfLinks = document.querySelectorAll('a[href$=".pdf"]');
  pdfLinks.forEach(link => {
    (link as HTMLAnchorElement).target = '_blank';
  });
}

/**
 * Set up scroll to top functionality
 * @private
 */
function setupScrollToTop(): void {
  const scrollToTopBtn = document.querySelector('.scroll-to-top') as HTMLElement;

  if (!scrollToTopBtn) return;

  // Show/hide button based on scroll position
  window.addEventListener('scroll', () => {
    if (window.scrollY > 250) {
      scrollToTopBtn.style.opacity = '1';
    } else {
      scrollToTopBtn.style.opacity = '0';
    }
  });

  // Handle click
  scrollToTopBtn.addEventListener('click', (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  // Handle keypress
  scrollToTopBtn.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });
}

/**
 * Set up sticky header functionality
 * @private
 */
function setupStickyHeader(): void {
  let scrollPos = window.scrollY;
  const header = document.querySelector('.header') as HTMLElement;

  if (!header) return;

  const headerHeight = header.offsetHeight + 100;
  const root = document.querySelector(':root') as HTMLElement;

  const addStuckClass = () => root.classList.add('stuck');
  const removeStuckClass = () => root.classList.remove('stuck');

  window.addEventListener('scroll', () => {
    scrollPos = window.scrollY;

    if (scrollPos >= headerHeight) {
      addStuckClass();
    } else {
      removeStuckClass();
    }
  });
}

/**
 * Set up search functionality
 * @private
 */
function setupSearch(): void {
  setupMainSearch();
  setupVoiceSearch();
  setupFormSearch();
}

function getSearchUrl(searchTerm: string): string {
  return '/search/?q=' + encodeURIComponent(searchTerm.trim());
}

/**
 * Set up voice search functionality
 * @private
 */
function setupVoiceSearch(): void {
  const searchContainers = document.querySelectorAll('.search-container');
  if (!searchContainers.length) return;

  const speechWindow = window as AtlasSpeechRecognitionWindow;
  const SpeechRecognitionConstructor = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;

  searchContainers.forEach(searchContainer => {
    const voiceSearchButton = searchContainer.querySelector('.mic-search') as HTMLElement;
    const searchInput = searchContainer.querySelector('.search-container__search-input') as HTMLInputElement;

    if (!voiceSearchButton || !searchInput) return;
    if (!SpeechRecognitionConstructor) {
      voiceSearchButton.setAttribute('aria-disabled', 'true');
      voiceSearchButton.setAttribute('title', 'Voice search is not supported in this browser');
      return;
    }

    const startVoiceSearch = () => {
      const recognition = new SpeechRecognitionConstructor();
      recognition.lang = document.documentElement.lang || 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript.trim();
        if (!transcript) return;

        searchInput.value = transcript;
        window.location.href = getSearchUrl(transcript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error || event.type);
      };

      recognition.onend = () => {
        voiceSearchButton.classList.remove('mic-search--listening');
        voiceSearchButton.removeAttribute('aria-pressed');
      };

      voiceSearchButton.classList.add('mic-search--listening');
      voiceSearchButton.setAttribute('aria-pressed', 'true');
      recognition.start();
    };

    voiceSearchButton.addEventListener('click', startVoiceSearch);
    voiceSearchButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        startVoiceSearch();
      }
    });
  });
}

/**
 * Set up main search toggle and functionality
 * @private
 */
// function setupMainSearch(): void {
//   const searchToggle = document.querySelector('.search-toggle');
//   const searchBox = document.querySelector('.search-box') as HTMLElement;

//   if (!searchToggle || !searchBox) return;

//   const handleSearchToggle = () => {
//     document.body.classList.add('search-open');
//     searchBox.style.display = 'block';

//     const searchInput = searchBox.querySelector('input') as HTMLInputElement;
//     if (searchInput) searchInput.focus();

//     searchToggle.setAttribute('aria-expanded', 'true');

//     setupSearchClose();
//     setupSearchFocusTrap();
//   };

//   const setupSearchClose = () => {
//     const closeIcon = searchBox.querySelector('.search-box__icon svg');

//     const closeSearch = () => {
//       searchBox.style.display = 'none';
//       document.body.classList.remove('search-open');
//       searchToggle.setAttribute('aria-expanded', 'false');
//     };

//     if (closeIcon) {
//       closeIcon.addEventListener('click', closeSearch);
//       closeIcon.addEventListener('keypress', (e: Event) => {
//         const keyEvent = e as KeyboardEvent;
//         if (keyEvent.key === 'Enter') closeSearch();
//       });
//     }
//   };

//   const setupSearchFocusTrap = () => {
//     const focusableElements = searchBox.querySelectorAll('.search-box__icon svg, .search-container .mic-search, .search-container a.btn-search, .search-container input');
//     const firstFocusable = focusableElements[0] as HTMLElement;
//     const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

//     if (lastFocusable) {
//       lastFocusable.addEventListener('keydown', (e) => {
//         if (e.key === 'Tab' && !e.shiftKey) {
//           e.preventDefault();
//           firstFocusable.focus();
//         }
//       });
//     }

//     if (firstFocusable) {
//       firstFocusable.addEventListener('keydown', (e) => {
//         if (e.key === 'Tab' && e.shiftKey) {
//           e.preventDefault();
//           lastFocusable.focus();
//         }
//       });
//     }
//   };

//   searchToggle.addEventListener('click', handleSearchToggle);
//   searchToggle.addEventListener('keypress', (e: Event) => {
//     const keyEvent = e as KeyboardEvent;
//     if (keyEvent.key === 'Enter') handleSearchToggle();
//   });

//   // Search input functionality
//   const searchInput = document.querySelector('.search-container input[type="text"]') as HTMLInputElement;
//   if (searchInput) {
//     searchInput.addEventListener('keypress', (e) => {
//       if (e.key === 'Enter') {
//         e.preventDefault();
//         const searchUrl = '/search/?keyword=' + encodeURIComponent(searchInput.value.replace(/&/g, '%26'));
//         window.location.href = searchUrl;
//       }
//     });
//   }

//   // Search container click
//   const searchContainer = document.querySelector('.search-container');
//   if (searchContainer) {
//     searchContainer.addEventListener('click', () => {
//       searchContainer.classList.add('search-open');
//     });
//   }
// }

function setupMainSearch(): void {
  const searchToggles = document.querySelectorAll('.search-toggle');
  const searchBox = document.querySelector('.search-box') as HTMLElement;

  if (!searchToggles.length || !searchBox) return;
  let focusTrapInitialized = false;

  const setSearchExpanded = (isExpanded: boolean) => {
    searchToggles.forEach(toggle => {
      toggle.setAttribute('aria-expanded', String(isExpanded));
    });
  };

  const setupSearchFocusTrap = () => {
    if (focusTrapInitialized) return;
    focusTrapInitialized = true;

    const focusableElements = searchBox.querySelectorAll(
      '.search-box__icon, .search-container input, .search-container a, .search-container button'
    );
    if (!focusableElements.length) return;
    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;
    lastFocusable.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        firstFocusable.focus();
      }
    });

    firstFocusable.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        lastFocusable.focus();
      }
    });
  };

  const openSearch = (toggle: HTMLElement) => {
    document.body.classList.add('search-open');
    searchBox.classList.add('open');
    const searchInput = searchBox.querySelector('input') as HTMLInputElement;
    if (searchInput) searchInput.focus();
    setSearchExpanded(true);
    setupSearchFocusTrap();
  };

  const closeSearch = () => {
    closeSearchBox();
  };

  const toggleSearch = (toggle: HTMLElement) => {
    if (document.documentElement.classList.contains('nav-wrap-open')) {
      closeNavigationMenu();
      openSearch(toggle);
      return;
    }

    if (searchBox.classList.contains('open')) {
      closeSearch();
      toggle.focus();
    } else {
      openSearch(toggle);
    }
  };


  // Bind all toggles
  searchToggles.forEach(toggle => {
    toggle.addEventListener('click', () => {
      toggleSearch(toggle as HTMLElement);
    });

    toggle.addEventListener('keypress', (e: Event) => {
      const keyEvent = e as KeyboardEvent;
      if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
        e.preventDefault();
        toggleSearch(toggle as HTMLElement);
      }
    });
  });

  // Close button
  const closeIcon = searchBox.querySelector('.search-box__icon');

  if (closeIcon) {
    closeIcon.addEventListener('click', closeSearch);
    closeIcon.addEventListener('keypress', (e: Event) => {
      const keyEvent = e as KeyboardEvent;
      if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
        e.preventDefault();
        closeSearch();
      }
    });

  }

  // Handle Enter key in search input
  const searchInput = searchBox.querySelector(
    '.search-container__search-input'
  ) as HTMLInputElement;
  if (searchInput) {
    searchInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        window.location.href = getSearchUrl(searchInput.value);
      }
    });
  }
}

/**
 * Set up form search functionality
 * @private
 */
function setupFormSearch(): void {
  const searchForm = document.querySelector('.search-form');
  if (!searchForm) return;

  const searchButton = searchForm.querySelector('button');
  const searchInput = searchForm.querySelector('#Keyword') as HTMLInputElement;

  if (searchButton) {
    searchButton.addEventListener('click', (e) => {
      e.preventDefault();
      const root = `${location.protocol}//${location.host}/search/?keyword=`;
      const terms = searchInput?.value || '';
      window.location.href = root + encodeURIComponent(terms);
    });
  }

  if (searchInput) {
    searchInput.addEventListener('keyup', (e) => {
      if (e.key === 'Enter') {
        const root = `${location.protocol}//${location.host}/search/?keyword=`;
        const terms = searchInput.value || '';
        window.location.href = root + encodeURIComponent(terms);
      }
    });

    searchInput.addEventListener('focusin', () => {
      searchForm.classList.add('active');
    });

    searchInput.addEventListener('focusout', () => {
      searchForm.classList.remove('active');
    });
  }
}

/**
 * Set up select element functionality
 * @private
 */
function setupSelect(): void {
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    if (target.tagName === 'SELECT') {
      target.classList.toggle('open');
    }

    if (target.tagName === 'LABEL') {
      const nextElement = target.nextElementSibling;
      if (nextElement && nextElement.tagName === 'SELECT') {
        e.preventDefault();
        (nextElement as HTMLSelectElement).focus();
      }
    }
  });

  document.addEventListener('keyup', (e) => {
    if (e.key === 'Escape') {
      const openSelects = document.querySelectorAll('select.open');
      openSelects.forEach(select => select.classList.remove('open'));
    }
  });

  window.addEventListener('scroll', () => {
    const openSelects = document.querySelectorAll('select.open');
    openSelects.forEach(select => select.classList.remove('open'));
  });
}

/**
 * Initialize speedbumps for external links
 * @private
 */
function setupSpeedbumps(): void {
  try {
    const safeLinks = [
      '[href*=".SITEDOMAIN.com"]',
      '[href^="https://SITEDOMAIN.com"]',
      '[href^="http://SITEDOMAIN.com"]',
      '[href^="http://www.SITEDOMAIN.com"]',
      '[href^="http://DEMO-URL.lrsws.co"]',
      '[href^="https://DEMO-URL.lrsws.co"]',
      '[class*="email"]',
      '[href^="javascript:"]',
      "[href*='iaf.infinity.com']",
      "[href*='sageworksanalyst.com']",
      "[href*='myaccountaccess.com']",
      "[href*='secure-loancenter.com']",
      "[href*='mortgage-application.net']",
      "[href*='online1.elancard.com']",
      "[href^='?udt']"
    ];

    if (typeof (window as any).initSpeedbump === 'function') {
      (window as any).initSpeedbump(safeLinks);
    }
  } catch (error) {
    console.warn('Speedbump initialization failed:', error);
  }
}

/**
 * Set up content banner functionality
 * @private
 */
function setupContentBanners(): void {
  // Wrap banner content
  const banners = document.querySelectorAll('.image-content-banner, .image-content-banner--solid');
  banners.forEach(banner => {
    const inner = document.createElement('div');
    inner.className = 'image-content-banner--inner';
    while (banner.firstChild) {
      inner.appendChild(banner.firstChild);
    }
    banner.appendChild(inner);
  });

  // Set banner image heights
  const setBannerHeights = () => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      const solidBannerImages = document.querySelectorAll('[class*=image-content-banner--solid] figure img');
      solidBannerImages.forEach(img => {
        const banner = (img as HTMLElement).closest('[class*=image-content-banner--solid]') as HTMLElement;
        if (banner) {
          (img as HTMLElement).style.minHeight = banner.offsetHeight + 'px';
        }
      });
    } else {
      const solidBannerImages = document.querySelectorAll('[class*=image-content-banner--solid] figure img');
      solidBannerImages.forEach(img => {
        (img as HTMLElement).style.removeProperty('min-height');
      });
    }
  };

  setBannerHeights();
  window.addEventListener('resize', setBannerHeights);

  // Featured boxed item height adjustment
  const setBoxMinHeight = () => {
    const containedImages = document.querySelectorAll('.featured-boxed-item.image-contained img');
    containedImages.forEach(img => {
      const imgElement = img as HTMLImageElement;
      const featuredBox = imgElement.closest('.featured-boxed-item') as HTMLElement;
      if (featuredBox) {
        featuredBox.style.minHeight = imgElement.offsetHeight + 'px';
      }
    });
  };

  setBoxMinHeight();
  window.addEventListener('resize', setBoxMinHeight);
}

/**
 * Set up flip card functionality
 * @private
 */
function setupFlipCards(): void {
  const flipCards = document.querySelectorAll('.flip-card');

  flipCards.forEach(card => {
    const cardElement = card as HTMLElement;

    // Add accessibility attributes
    cardElement.setAttribute('aria-expanded', 'false');
    cardElement.setAttribute('role', 'button');
    cardElement.setAttribute('tabindex', '0');

    // Add toggle button
    const toggle = document.createElement('div');
    toggle.className = 'flip-card__toggle';
    cardElement.appendChild(toggle);

    const setInnerHeight = () => {
      const front = cardElement.querySelector('.flip-card__front') as HTMLElement;
      const back = cardElement.querySelector('.flip-card__back') as HTMLElement;
      const inner = cardElement.querySelector('.flip-card__inner') as HTMLElement;

      if (front && back && inner) {
        const maxHeight = Math.max(front.offsetHeight, back.offsetHeight);
        inner.style.height = maxHeight + 'px';
      }
    };

    const toggleCard = () => {
      cardElement.classList.toggle('flip-card--flip-over');
      const isExpanded = cardElement.classList.contains('flip-card--flip-over');
      cardElement.setAttribute('aria-expanded', isExpanded.toString());
      setInnerHeight();
    };

    cardElement.addEventListener('click', toggleCard);
    cardElement.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleCard();
      }
    });

    // Set initial height
    setInnerHeight();
  });
}

/**
 * Set up textured backgrounds
 * @private
 */
function setupTexturedBackgrounds(): void {
  const texturedSections = document.querySelectorAll('.content-section--textured');
  if (texturedSections.length > 0 && typeof (window as any).initTexturedBackgrounds === 'function') {
    (window as any).initTexturedBackgrounds();
  }
}

/**
 * Set up miscellaneous functionality
 * @private
 */
function setupMiscellaneous(): void {
  // Replace b and i tags with strong and em
  const boldTags = document.querySelectorAll('main b');
  boldTags.forEach(b => {
    const strong = document.createElement('strong');
    while (b.firstChild) {
      strong.appendChild(b.firstChild);
    }
    b.replaceWith(strong);
  });

  const italicTags = document.querySelectorAll('main i');
  italicTags.forEach(i => {
    const em = document.createElement('em');
    while (i.firstChild) {
      em.appendChild(i.firstChild);
    }
    i.replaceWith(em);
  });

  // Style adjustment functionality
  setupStyleAdjustments();

  // Initialize external plugins
  if (typeof (window as any).articleColumns === 'function') {
    (window as any).articleColumns();
  }

  const mainElement = document.querySelector('#main');
  if (mainElement && typeof (mainElement as any).responsiveTables === 'function') {
    (mainElement as any).responsiveTables();
  }
}

/**
 * Set up style adjustment controls
 * @private
 */
function setupStyleAdjustments(): void {
  const headerBg = document.querySelector('.header-bg') as HTMLSelectElement;
  if (headerBg) {
    headerBg.addEventListener('change', () => {
      const root = document.querySelector(':root') as HTMLElement;
      root.classList.remove('standard', 'transparent-nav');
      root.classList.add(headerBg.value);
    });
  }

  const headerLayout = document.querySelector('.header-layout') as HTMLSelectElement;
  if (headerLayout) {
    headerLayout.addEventListener('change', () => {
      const root = document.querySelector(':root') as HTMLElement;
      root.classList.remove('standard', 'centered');
      root.classList.add(headerLayout.value);
    });
  }

  const navLayout = document.querySelector('.nav-layout') as HTMLSelectElement;
  if (navLayout) {
    navLayout.addEventListener('change', () => {
      const navigation = document.querySelector('.navigation');
      if (navigation) {
        navigation.classList.remove('standard', 'accordion-nav', 'slide-nav');
        navigation.classList.add(navLayout.value);
      }
    });
  }

  const mode = document.querySelector('.mode') as HTMLSelectElement;
  if (mode) {
    mode.addEventListener('change', () => {
      document.body.classList.remove('dark-mode', 'light-mode');
      document.body.classList.add(mode.value);
    });
  }
}

/**
 * AtlasTheme manager class mirroring the Subpage pattern.
 * Provides a simple facade over existing functions and plugin access.
 */
class AtlasTheme {
  constructor() {
    this.initialize();
  }

  private initialize(): void {
    try {
      init();
      initializeMain();
    } catch (err) {
      console.error('Atlas init error:', err);
    }
  }

  // Public API mirrors existing helpers
  public refresh(): void {
    try {
      refresh();
    } catch (err) {
      console.error('Atlas refresh error:', err);
    }
  }

  public lazyLoadImages(): void {
    try {
      lazyLoadImages();
    } catch (err) {
      console.error('Atlas lazy-load error:', err);
    }
  }

  public getPlugin(name: string): any {
    return getPlugin(name);
  }
}

// Global instance for compatibility/access
let atlasTheme: AtlasTheme | null = null;

// Initialize on DOM ready and expose to window
document.addEventListener('DOMContentLoaded', () => {
  atlasTheme = new AtlasTheme();
  (window as any).atlasTheme = atlasTheme;
});

// Expose utility functions globally
(window as any).updateAriaLabels = updateAriaLabels;
